import React, { useState, useEffect } from 'react';
import { Edit, Plus } from 'lucide-react';
import FormModal from './FormModal';
import { usersApi } from '@domains/admin/api/users.api';

interface UserTypeModalProps {
  onClose: () => void;
  onSubmit: (userType: any) => void | Promise<void>;
  selectedUserType?: any;
}

export const UserTypeModal: React.FC<UserTypeModalProps> = ({
  onClose,
  onSubmit,
  selectedUserType,
}) => {
  const [userType, setUserType] = useState(
    selectedUserType || {
      name: '',
      description: '',
      permissions: [] as string[],
    }
  );
  const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  useEffect(() => {
    loadAvailablePermissions();
  }, [selectedUserType?.id]);

  useEffect(() => {
    if (selectedUserType) {
      setUserType({
        name: selectedUserType.name || '',
        description: selectedUserType.description || '',
        permissions: selectedUserType.permissions || [],
      });
    }
  }, [selectedUserType]);

  const loadAvailablePermissions = async () => {
    try {
      // When editing an existing permission profile, load all permissions to show existing assignments
      // When creating new, only load assignable permissions
      const isEditing = selectedUserType?.id;
      const permissions = isEditing
        ? await usersApi.getAllPermissions()
        : await usersApi.getAssignablePermissions();
      setAvailablePermissions(permissions);
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  const handleSubmit = async (data: any) => {
    await onSubmit({ ...userType, ...data });
    onClose();
  };

  const isCreate = !selectedUserType?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Permission Profile' : 'Edit Permission Profile'}
      subtitle={isCreate ? 'Create a new permission profile with specific permissions' : 'Modify existing permission profile'}
      icon={isCreate ? <Plus className="text-white" size={24} /> : <Edit className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Permission Profile' : 'Update Permission Profile'}
      cancelText="Cancel"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-slate-700">
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={userType.name}
            onChange={(e) => setUserType({ ...userType, name: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="e.g., Compliance Officer"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="description" className="block text-sm font-medium text-slate-700">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={userType.description}
            onChange={(e) => setUserType({ ...userType, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="Describe the role and responsibilities..."
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            Permissions
          </label>
          {isLoadingPermissions ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
              <span className="ml-2 text-sm text-slate-600">Loading permissions...</span>
            </div>
          ) : (
            <div className="max-h-60 overflow-y-auto border border-slate-300 rounded-xl p-3 bg-white">
              {availablePermissions.length > 0 ? (
                availablePermissions.map(permission => (
                  <label key={permission} className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      checked={userType.permissions.includes(permission)}
                      onChange={(e) => {
                        const newPermissions = e.target.checked
                          ? [...userType.permissions, permission]
                          : userType.permissions.filter((p: string) => p !== permission);
                        setUserType({ ...userType, permissions: newPermissions });
                      }}
                      className="rounded border-slate-300 text-slate-600 focus:ring-slate-500"
                    />
                    <span className="text-sm text-slate-700">{permission}</span>
                  </label>
                ))
              ) : (
                <div className="text-sm text-slate-500 py-4 text-center">
                  No permissions available
                </div>
              )}
            </div>
          )}
          <p className="text-xs text-slate-500">
            Select the permissions this permission profile should have. Users assigned this profile will inherit these permissions.
          </p>
        </div>
      </div>
    </FormModal>
  );
};